import React, { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { Helmet } from 'react-helmet';
import { camelCase } from 'lodash';
import {
    IfNotMobile,
    CMSContent,
    StyleOverrides,
    scrollToHashElement,
    ErrorBoundary,
    TeleScript,
    getCMSObject,
    getInterpolatedPageData,
    FeatureLockout,
    DisplayContent,
    TitleBreadcrumb,
} from 'sg-ui-components';

import { Header } from './Layouts/Header';
import WrapperHeader from './Layouts/Wrapper/WrapperHeader';
import WrapperFooter from './Layouts/Wrapper/WrapperFooter';

import IdleTimeout from './IdleTimeout';
import LoadingIndicator from './LoadingIndicator';
import LoggedInOnlyPages from '../Components/LoggedInOnlyPages';
import PointsDashboard from './PointsDashboard';

import siteConfig from '../promotionConfig';
import UserLockout from '../utils/user_lockout';

const Layout = ({ user, config, cmsSourceFirebase, actions, children, match, configReplacements }) => {
    const path = match?.path ?? '';
    const pageData = getInterpolatedPageData(config?.site?.routes, match, configReplacements);
    const errorBoundaryFallback = getCMSObject('data.messages.pageErrorMessages.jsonBlock')?.[`${camelCase(path) || 'home'}Fallback`];
    const hasPointsDashboard = config?.site?.loyaltySettings?.hasPointsDashboard;
    //? Note: Frontend config will be requested and stored on first pageload / refresh
    useEffect(() => {
        async function configStateActions() {
            await actions.configActions.getEnvironmentConfig();
        }

        if (!config?.config?.firebase) {
            configStateActions();
            // console.log('🟢 Initialization: Environment config set');
        }
    }, []);

    //? Note: Firebase CMS webContent and webMenu will be requested and stored on first pageload / refresh
    useEffect(() => {
        async function pageStateActions() {
            await actions.cmsSourceFirebaseActions.storeAllData({
                config: siteConfig?.testing?.firebase ?? config.config.firebase,
                docName: 'webConfig',
            });

            await actions.cmsSourceFirebaseActions.storeAllData({
                config: siteConfig?.testing?.firebase ?? config.config.firebase,
                docName: 'webContent',
            });

            await actions.cmsSourceFirebaseActions.storeAllData({
                config: siteConfig?.testing?.firebase ?? config.config.firebase,
                docName: 'webMenu',
            });

            await actions.cmsSourceFirebaseActions.setLoaded(true);

            const webConfigData = getCMSObject('data', 'webConfig') ?? {};
            const siteConfigWithOutFunction = JSON.parse(JSON.stringify(siteConfig));
            const siteConfigFromCMS = JSON.parse(webConfigData?.siteConfig ?? '{}') ?? {};
            const siteConfigData = { ...siteConfigWithOutFunction, ...siteConfigFromCMS };
            delete webConfigData.siteConfig;
            delete siteConfigData.testing;

            // Set config store
            await actions.configActions.setWebConfig(webConfigData);
            await actions.configActions.setSiteConfig(siteConfigData);
            await actions.configActions.setLoaded(true);

            scrollToHashElement();
        }

        if (config?.config?.firebase && !cmsSourceFirebase?.loaded) {
            pageStateActions();
            // console.log('🟢 Initialization: CMS Data and Config Stores synced');
        }
    }, [config]);

    useEffect(() => {
        async function playerStateActions() {
            await actions.userActions.getPlayerData();
            scrollToHashElement();
            if (user?.loggedIn && config?.web?.features?.rewardsNotifications) {
                // Set notifications
                await actions.notificationActions.getNotifications();
            }
        }

        playerStateActions();
        // console.log('🟢 Initialization: Player validation completed');
    }, [user?.loggedIn, config?.web?.features?.rewardsNotifications]);

    // Get GTM ID based on the environment from the config api
    const gtmId = config?.config?.gtm_id;
    if (gtmId) {
        const tagManagerArgs = {
            gtmId,
        };
        TagManager.initialize(tagManagerArgs);
    }

    //? Redirect players to login page if the page is logged in only page and the players are not logged in
    if (pageData?.loggedInOnly && !user.loggedIn) {
        return <LoggedInOnlyPages />;
    }

    const isLoaded = config?.loaded && cmsSourceFirebase.loaded;

    if (!isLoaded) {
        return <LoadingIndicator priority='critical' />;
    }

    return (
        <FeatureLockout config={config} page={pageData} user={user} options={{ redirectPath: pageData?.featureDisabledRedirectPath }}>
            <Helmet defaultTitle='Georgia Lottery' titleTemplate='%s | Georgia Lottery'>
                <title>{pageData?.title}</title>
            </Helmet>
            <IfNotMobile>
                <CMSContent
                    cmsSourceFirebase={cmsSourceFirebase}
                    localStorageObject='webContent'
                    contentPath='data.webNotices'
                    className='fixed-top'
                    noticePlacement='web-notice-top-fixed'
                />
                <CMSContent
                    cmsSourceFirebase={cmsSourceFirebase}
                    localStorageObject='webContent'
                    contentPath='data.webNotices'
                    className=''
                    noticePlacement='web-notice-top'
                />
                <WrapperHeader />
            </IfNotMobile>
            <div className={`content-root ${pageData?.className}`}>
                <ErrorBoundary fallback={<TeleScript line={errorBoundaryFallback} />} details={{ pageData, user, config }}>
                    <Header />
                    <CMSContent
                        cmsSourceFirebase={cmsSourceFirebase}
                        localStorageObject='webContent'
                        contentPath='data.webNotices'
                        className=''
                        noticePlacement='web-notice-below-header'
                    />

                    <div className={`${pageData?.className}-container`}>
                        <div className='galaxy-fold-open-your-device d-none'></div>
                        <UserLockout currentSection={pageData?.className} disableRedirect={pageData?.className}>
                            <DisplayContent note='Show Points Dashboard' isVisible={hasPointsDashboard}>
                                <div>{pageData.className !== 'account-confirm' && <PointsDashboard />}</div>
                            </DisplayContent>
                            <div className='layout container'>
                                <div className='main row'>
                                    <div className='page-content w-100'>
                                        <DisplayContent note='Show page related Title Breadcrumbs' isVisible={config?.web?.features?.titleBreadcrumbs}>
                                            {path && <TitleBreadcrumb pageData={pageData} />}
                                        </DisplayContent>

                                        {children}
                                    </div>
                                </div>
                            </div>
                        </UserLockout>
                        <IdleTimeout />
                    </div>
                </ErrorBoundary>
            </div>
            <IfNotMobile>
                <CMSContent
                    cmsSourceFirebase={cmsSourceFirebase}
                    localStorageObject='webContent'
                    contentPath='data.webNotices'
                    className='fixed-bottom'
                    noticePlacement='web-notice-bottom-fixed'
                />
                <WrapperFooter />
            </IfNotMobile>
            <StyleOverrides cmsSourceFirebase={cmsSourceFirebase} />
        </FeatureLockout>
    );
};

export default Layout;
